import React, { useEffect, useState, useMemo } from 'react';
import { Calculator, TrendingDown, DollarSign, Calendar, PieChart, Building2, ArrowRight, Info, ChevronDown, ChevronUp } from 'lucide-react';
import './EducationLoanEMICalculator.css';

const RATE_DATA_CHECKED_ON = '2026-08-25';
const EUR_TO_INR = 111.74; // Build-time fallback; runtime reference-rate fetch refreshes this.
const FX_RATE_ENDPOINT = 'https://api.frankfurter.dev/v2/rate/EUR/INR';
export const TENURE_OPTIONS = [5, 10, 15, 20];
export const LOAN_AMOUNT_PRESETS = [1000000, 1500000, 2000000, 2500000, 3000000, 5000000];

export const calculateLoanEMI = (principal, annualRate, months) => {
  const amount = Number(principal);
  const rate = Number(annualRate);
  const term = Number(months);
  if (!Number.isFinite(amount) || !Number.isFinite(rate) || !Number.isFinite(term) || amount < 0 || rate < 0 || term <= 0) {
    return 0;
  }

  const monthlyRate = rate / 12 / 100;
  if (monthlyRate === 0) return amount / term;
  return (amount * monthlyRate * Math.pow(1 + monthlyRate, term)) /
    (Math.pow(1 + monthlyRate, term) - 1);
};

export const calculateMonthlyInterest = (principal, annualRate) => {
  const amount = Number(principal);
  const rate = Number(annualRate);
  if (!Number.isFinite(amount) || !Number.isFinite(rate) || amount < 0 || rate < 0) return 0;
  return amount * (rate / 12 / 100);
};

// Indicative lender data. Final pricing varies by profile, institute,
// collateral, benchmark, concessions, and lender underwriting.
export const BANKS = {
  sbi: {
    id: 'sbi',
    name: 'SBI',
    fullName: 'State Bank of India',
    rate: 9.15,
    rateDisplay: '8.65%-9.15% floating',
    loanLimit: 'Up to ₹3 crore',
    collateral: 'Up to ₹50 lakh collateral-free for select institutions',
    tenure: 'Up to 15 years',
    logo: '🏦',
    features: ['Global Ed-Vantage for overseas study', '0.50% girl-student concession may apply', 'Course + 6-month moratorium published'],
    processingFee: '₹10,000 + applicable taxes',
    source: 'SBI Education Loan Scheme / Global Ed-Vantage published rates',
  },
  hdfc: {
    id: 'hdfc',
    name: 'HDFC Credila',
    fullName: 'HDFC Credila Financial Services',
    rate: 9.95,
    rateDisplay: 'From 9.95% floating',
    loanLimit: 'Up to 100% financing, profile-based',
    collateral: 'Profile and credit-assessment based',
    tenure: 'Up to 15 years',
    logo: '🏛️',
    features: ['Overseas-study financing', 'Up to 100% financing may be available', 'Zero prepayment/foreclosure charges on published product page'],
    processingFee: '1.25% + GST on published B.Tech offer',
    source: 'Credila published education-loan product information',
  },
  axis: {
    id: 'axis',
    name: 'Axis Bank',
    fullName: 'Axis Bank Education Loan',
    rate: 10.5,
    rateDisplay: 'Profile-based; default planning input 10.50%',
    loanLimit: 'Up to ₹3 crore',
    collateral: 'Secured and unsecured options, profile-based',
    tenure: 'Up to 15 years',
    logo: '🏢',
    features: ['International and domestic study', 'Pre-admission sanction available', 'No foreclosure charges stated on product page'],
    processingFee: 'Confirm current quote',
    source: 'ICICI Bank education-loan product page; numeric rate varies by profile',
  },
  icici: {
    id: 'icici',
    name: 'ICICI Bank',
    fullName: 'ICICI Bank Education Loan',
    rate: 10.81,
    rateDisplay: '10.81%-12.24% published abroad range*',
    loanLimit: 'Profile and product based',
    collateral: 'Profile and security based',
    tenure: 'Confirm current product',
    logo: '🏪',
    features: ['Study-abroad education loan', 'Published rate varies by loan size', 'Final rate depends on credit and collateral'],
    processingFee: 'Confirm current quote',
    source: 'Axis Bank published education-loan rate sheet; range shown is dated July-September 2025',
  },
  bob: {
    id: 'bob',
    name: 'Bank of Baroda',
    fullName: 'Bank of Baroda Education Loan',
    rate: 10.5,
    rateDisplay: 'Profile-based; default planning input 10.50%',
    loanLimit: 'Product and profile based',
    collateral: 'Product and profile based',
    tenure: 'Confirm current product',
    logo: '🏛️',
    features: ['Government bank', 'Government subsidy schemes may apply', 'Rate linked to bank scheme and benchmark'],
    processingFee: 'Confirm current quote',
    source: 'Bank of Baroda product information; current numeric rate not publicly confirmed',
  },
};

const EducationLoanEMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState(2000000); // ₹20L default
  const [interestRate, setInterestRate] = useState(BANKS.hdfc.rate);
  const [tenure, setTenure] = useState(10); // years
  const [selectedBank, setSelectedBank] = useState('hdfc');
  const [currency, setCurrency] = useState('INR');
  const [prePayment, setPrePayment] = useState(0);
  const [showAmortization, setShowAmortization] = useState(false);
  const [showPrepaymentCalc, setShowPrepaymentCalc] = useState(false);
  const [eurToInr, setEurToInr] = useState(EUR_TO_INR);
  const [exchangeRateDate, setExchangeRateDate] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 5000);

    fetch(FX_RATE_ENDPOINT, { signal: controller.signal, headers: { Accept: 'application/json' } })
      .then(response => {
        if (!response.ok) throw new Error(`Exchange-rate request failed: ${response.status}`);
        return response.json();
      })
      .then(payload => {
        const rate = Number(payload?.rate);
        if (!Number.isFinite(rate) || rate <= 0) throw new Error('Invalid EUR to INR rate');
        if (!cancelled) {
          setEurToInr(rate);
          setExchangeRateDate(payload.date || null);
        }
      })
      .catch(error => {
        if (!cancelled && (error.name !== 'AbortError' || timedOut)) setExchangeRateDate(null);
      })
      .finally(() => window.clearTimeout(timeoutId));

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, []);

  // Main calculations
  const calculations = useMemo(() => {
    const months = tenure * 12;
    const emi = calculateLoanEMI(loanAmount, interestRate, months);
    const totalPayment = emi * months;
    const totalInterest = totalPayment - loanAmount;
    
    // Pre-payment calculations
    const prePaymentSavings = prePayment > 0 ? {
      newPrincipal: loanAmount - prePayment,
      newEMI: calculateLoanEMI(loanAmount - prePayment, interestRate, months),
      interestSaved: 0,
    } : null;
    
    if (prePaymentSavings) {
      const newTotalPayment = prePaymentSavings.newEMI * months;
      prePaymentSavings.interestSaved = totalInterest - (newTotalPayment - prePaymentSavings.newPrincipal);
    }

    // Amortization schedule (first 12 months)
    const schedule = [];
    let balance = loanAmount;
    const monthlyRate = interestRate / 12 / 100;
    
    for (let i = 1; i <= Math.min(12, months); i++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = emi - interestPayment;
      balance -= principalPayment;
      
      schedule.push({
        month: i,
        emi: emi,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance),
      });
    }

    return {
      emi,
      totalPayment,
      totalInterest,
      principalPercentage: ((loanAmount / totalPayment) * 100).toFixed(1),
      interestPercentage: ((totalInterest / totalPayment) * 100).toFixed(1),
      monthlyRatePercentage: monthlyRate * 100,
      firstMonthInterest: calculateMonthlyInterest(loanAmount, interestRate),
      prePaymentSavings,
      schedule,
    };
  }, [loanAmount, interestRate, tenure, prePayment]);

  // Format currency
  const formatCurrency = (amount, curr = currency) => {
    if (curr === 'EUR') {
      return `€${(amount / eurToInr).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Handle bank selection
  const handleBankSelect = (bankId) => {
    setSelectedBank(bankId);
    setInterestRate(BANKS[bankId].rate);
  };

  const amountInSelectedCurrency = currency === 'EUR' ? loanAmount / eurToInr : loanAmount;
  const prePaymentInSelectedCurrency = currency === 'EUR' ? prePayment / eurToInr : prePayment;
  const currencyRateLabel = exchangeRateDate
    ? `EUR reference rate: ₹${eurToInr.toFixed(2)} on ${exchangeRateDate}`
    : `Using fallback EUR reference rate: ₹${eurToInr.toFixed(2)}`;

  return (
    <div className="emi-calculator">
      <div className="calculator-header">
        <h2><Calculator size={28} /> Education Loan EMI Calculator</h2>
        <p className="calculator-subtitle">Plan your education loan for Ireland studies with standard EMI calculations. Lender terms checked {RATE_DATA_CHECKED_ON}; verify the final quote before applying.</p>
        <div className="currency-toggle">
          <button 
            className={`currency-btn ${currency === 'INR' ? 'active' : ''}`}
            onClick={() => setCurrency('INR')}
          >
            ₹ INR
          </button>
          <button 
            className={`currency-btn ${currency === 'EUR' ? 'active' : ''}`}
            onClick={() => setCurrency('EUR')}
          >
            € EUR
          </button>
        </div>
      </div>

      <div className="calculator-body">
        {/* Bank Selection */}
        <section className="bank-selection-section">
          <h3><Building2 size={20} /> Select Bank</h3>
          <div className="bank-cards-grid">
            {Object.values(BANKS).map((bank) => (
              <div
                key={bank.id}
                className={`bank-card ${selectedBank === bank.id ? 'selected' : ''}`}
                onClick={() => handleBankSelect(bank.id)}
              >
                <div className="bank-logo">{bank.logo}</div>
                <div className="bank-info">
                  <span className="bank-name">{bank.name}</span>
                  <span className="bank-rate">{bank.rateDisplay}</span>
                </div>
                {selectedBank === bank.id && <div className="selected-indicator">✓</div>}
              </div>
            ))}
          </div>
          {selectedBank && (
              <div className="bank-details">
                <div className="bank-full-name">{BANKS[selectedBank].fullName}</div>
                <div className="bank-offer-summary">
                  <span>{BANKS[selectedBank].loanLimit}</span>
                  <span>{BANKS[selectedBank].collateral}</span>
                  <span>{BANKS[selectedBank].tenure}</span>
                </div>
              <div className="bank-features">
                {BANKS[selectedBank].features.map((feature, idx) => (
                  <span key={idx} className="feature-tag">✓ {feature}</span>
                ))}
              </div>
              <div className="processing-fee">
                <Info size={14} /> Processing Fee: {BANKS[selectedBank].processingFee}
              </div>
              <div className="bank-source">{BANKS[selectedBank].source}</div>
            </div>
          )}
        </section>

        {/* Loan Parameters */}
        <section className="parameters-section">
          <h3><DollarSign size={20} /> Loan Parameters</h3>
          
          {/* Loan Amount Slider */}
          <div className="slider-group">
            <div className="slider-header">
              <label>Loan Amount</label>
              <span className="slider-value">{formatCurrency(loanAmount)}</span>
            </div>
            <div className="amount-input-row">
              <span className="amount-input-prefix">{currency === 'EUR' ? '€' : '₹'}</span>
              <input
                type="number"
                min={currency === 'EUR' ? 500000 / eurToInr : 500000}
                max={currency === 'EUR' ? 50000000 / eurToInr : 50000000}
                step={currency === 'EUR' ? 100 : 10000}
                value={Math.round(amountInSelectedCurrency)}
                onChange={(e) => {
                  const selectedAmount = Number(e.target.value);
                  if (Number.isFinite(selectedAmount)) {
                    setLoanAmount(Math.round(selectedAmount * (currency === 'EUR' ? eurToInr : 1)));
                  }
                }}
                className="amount-input"
                aria-label={`Loan amount in ${currency}`}
              />
            </div>
            <input
              type="range"
              min={currency === 'EUR' ? 500000 / eurToInr : 500000}
              max={currency === 'EUR' ? 50000000 / eurToInr : 50000000}
              step={currency === 'EUR' ? 1000 / eurToInr : 100000}
              value={amountInSelectedCurrency}
              onChange={(e) => setLoanAmount(Math.round(Number(e.target.value) * (currency === 'EUR' ? eurToInr : 1)))}
              className="slider"
            />
            <div className="slider-labels">
              <span>{formatCurrency(500000)}</span>
              <span>{formatCurrency(2500000)}</span>
              <span>{formatCurrency(50000000)}</span>
            </div>
            <div className="loan-amount-presets" role="group" aria-label="Common loan amount choices">
              {LOAN_AMOUNT_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={loanAmount === preset ? 'active' : ''}
                  aria-pressed={loanAmount === preset}
                  onClick={() => setLoanAmount(preset)}
                >
                  {formatCurrency(preset)}
                </button>
              ))}
            </div>
          </div>

          {/* Interest Rate Slider */}
          <div className="slider-group">
            <div className="slider-header">
              <label>Interest Rate</label>
              <span className="slider-value">{interestRate}% p.a.</span>
            </div>
            <input
              type="range"
              min="8.5"
              max="14"
              step="0.25"
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              className="slider"
            />
            <div className="slider-labels">
              <span>8.5%</span>
              <span>11%</span>
              <span>14%</span>
            </div>
          </div>

          {/* Tenure Slider */}
          <div className="slider-group">
            <div className="slider-header">
              <label>Loan Tenure</label>
              <span className="slider-value">{tenure} years</span>
            </div>
            <input
              type="range"
              min="5"
              max="20"
              step="1"
              value={tenure}
              onChange={(e) => setTenure(Number(e.target.value))}
              className="slider"
            />
            <div className="slider-labels">
              <span>5 yrs</span>
              <span>10 yrs</span>
              <span>15 yrs</span>
              <span>20 yrs</span>
            </div>
            <div className="tenure-presets" role="group" aria-label="Common loan tenure choices">
              {TENURE_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={tenure === option ? 'active' : ''}
                  aria-pressed={tenure === option}
                  onClick={() => setTenure(option)}
                >
                  {option} years
                </button>
              ))}
            </div>
            <p className="calculation-note">
              Monthly rate: {(calculations.monthlyRatePercentage).toFixed(3)}% | First-month interest: {formatCurrency(calculations.firstMonthInterest)}
            </p>
          </div>
        </section>

        {/* Results Section */}
        <section className="results-section">
          <h3><PieChart size={20} /> EMI Breakdown</h3>
          
          <div className="results-grid">
            <div className="result-card primary">
              <div className="result-icon">💰</div>
              <div className="result-content">
                <span className="result-label">Monthly EMI</span>
                <span className="result-value">{formatCurrency(calculations.emi)}</span>
              </div>
            </div>
            
            <div className="result-card">
              <div className="result-icon">📊</div>
              <div className="result-content">
                <span className="result-label">Total Interest</span>
                <span className="result-value">{formatCurrency(calculations.totalInterest)}</span>
              </div>
            </div>
            
            <div className="result-card">
              <div className="result-icon">💵</div>
              <div className="result-content">
                <span className="result-label">Total Payment</span>
                <span className="result-value">{formatCurrency(calculations.totalPayment)}</span>
              </div>
            </div>
          </div>

          {/* Pie Chart Visualization */}
          <div className="chart-container">
            <div className="pie-chart">
              <div 
                className="pie-segment principal" 
                style={{ '--percentage': calculations.principalPercentage }}
              ></div>
              <div 
                className="pie-segment interest" 
                style={{ '--percentage': calculations.interestPercentage }}
              ></div>
              <div className="pie-center">
                <span className="pie-label">Principal</span>
                <span className="pie-value">{calculations.principalPercentage}%</span>
              </div>
            </div>
            <div className="chart-legend">
              <div className="legend-item">
                <span className="legend-color principal"></span>
                <span className="legend-text">Principal: {formatCurrency(loanAmount)}</span>
              </div>
              <div className="legend-item">
                <span className="legend-color interest"></span>
                <span className="legend-text">Interest: {formatCurrency(calculations.totalInterest)}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Amortization Schedule */}
        <section className="amortization-section">
          <button 
            className="section-toggle"
            onClick={() => setShowAmortization(!showAmortization)}
          >
            <Calendar size={20} />
            <span>Amortization Schedule (First 12 Months)</span>
            {showAmortization ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          
          {showAmortization && (
            <div className="amortization-table-container">
              <table className="amortization-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>EMI</th>
                    <th>Principal</th>
                    <th>Interest</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {calculations.schedule.map((row) => (
                    <tr key={row.month}>
                      <td>{row.month}</td>
                      <td>{formatCurrency(row.emi)}</td>
                      <td>{formatCurrency(row.principal)}</td>
                      <td>{formatCurrency(row.interest)}</td>
                      <td>{formatCurrency(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Pre-payment Calculator */}
        <section className="prepayment-section">
          <button 
            className="section-toggle"
            onClick={() => setShowPrepaymentCalc(!showPrepaymentCalc)}
          >
            <TrendingDown size={20} />
            <span>Pre-payment Calculator</span>
            {showPrepaymentCalc ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          
          {showPrepaymentCalc && (
            <div className="prepayment-content">
              <div className="prepayment-input">
                <label>One-time Pre-payment Amount</label>
                <div className="prepayment-slider-group">
                  <input
                    type="range"
                    min="0"
                    max={currency === 'EUR' ? (loanAmount * 0.5) / eurToInr : loanAmount * 0.5}
                    step={currency === 'EUR' ? 1000 / eurToInr : 10000}
                    value={prePaymentInSelectedCurrency}
                    onChange={(e) => setPrePayment(Math.round(Number(e.target.value) * (currency === 'EUR' ? eurToInr : 1)))}
                    className="slider"
                  />
                  <span className="prepayment-value">{formatCurrency(prePayment)}</span>
                </div>
              </div>
              
              {prePayment > 0 && calculations.prePaymentSavings && (
                <div className="prepayment-results">
                  <div className="savings-card">
                    <div className="savings-icon">🎉</div>
                    <div className="savings-content">
                      <span className="savings-label">Interest Saved</span>
                      <span className="savings-value">{formatCurrency(calculations.prePaymentSavings.interestSaved)}</span>
                    </div>
                  </div>
                  <div className="savings-details">
                    <div className="detail-row">
                      <span>New EMI after pre-payment:</span>
                      <strong>{formatCurrency(calculations.prePaymentSavings.newEMI)}</strong>
                    </div>
                    <div className="detail-row">
                      <span>Reduced principal:</span>
                      <strong>{formatCurrency(calculations.prePaymentSavings.newPrincipal)}</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Quick Info */}
        <section className="info-section">
          <h4><Info size={18} /> Important Information</h4>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-icon">📚</span>
              <span>Collateral-free loans up to ₹7.5L under govt schemes</span>
            </div>
            <div className="info-item">
              <span className="info-icon">🎓</span>
              <span>Interest subsidy available for economically weaker sections</span>
            </div>
            <div className="info-item">
              <span className="info-icon">⏰</span>
              <span>Moratorium period: Course duration + 1 year</span>
            </div>
            <div className="info-item">
              <span className="info-icon">💱</span>
              <span>{currencyRateLabel}</span>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="calculator-cta">
          <h4>Need Help with Education Loan?</h4>
          <p>Compare illustrative lender rates and calculate repayments. Confirm current rates, fees, eligibility, and prepayment terms directly with the lender before applying.</p>
          <a href="/resources/education-loan-ireland-studies" className="cta-button">
            Read Complete Guide <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default EducationLoanEMICalculator;
